// File: src/pages/StateOfAiInLogistics.js
import React, { useLayoutEffect, useMemo, useRef } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { marked } from "marked";
import reportMd from "../data/aiInLogisticsReport";
import "./StateOfAiInLogistics.css";

const TAG_CLASS = {
  HIGH: "high",
  MEDIUM: "med",
  LOW: "low",
  UNKNOWN: "unk",
  vendor: "vendor",
  "3p": "high",
  api: "high",
  computed: "computed",
  "peer-reviewed": "high",
};

const TAG_RE = /\[((HIGH|MEDIUM|LOW|UNKNOWN|vendor|3p|api|computed|peer-reviewed)(?:[^[\]]{0,250})?)\]/g;

function buildHtml(md) {
  // The masthead below carries the H1, so drop it from the body.
  const body = md.replace(/^# .*\n/, "");
  let html = marked.parse(body, { gfm: true, breaks: false });
  html = html
    .replace(/<table>/g, '<div class="soail-tablewrap"><table>')
    .replace(/<\/table>/g, "</table></div>");
  const markTags = (input) =>
    input.replace(TAG_RE, (match, inner, kind) => {
      const cls = TAG_CLASS[kind] || "high";
      return `<span class="soail-tag ${cls}">${inner}</span>`;
    });
  // Twice, so a label that wraps another label ([HIGH ... [computed]]) also gets marked.
  return markTags(markTags(html));
}

function slugify(text) {
  return (
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 60) || "section"
  );
}

export default function StateOfAiInLogistics() {
  const html = useMemo(() => buildHtml(reportMd), []);
  const docRef = useRef(null);

  // Give every heading an id, so sections can be linked to directly.
  useLayoutEffect(() => {
    const doc = docRef.current;
    if (!doc) return;
    const used = {};
    doc.querySelectorAll("h2, h3").forEach((heading) => {
      const base = slugify(heading.textContent);
      let id = base;
      let n = 2;
      while (used[id]) id = `${base}-${n++}`;
      used[id] = true;
      heading.id = id;
    });
    // If the URL carries a hash (a link from the companion course), scroll to it once ids exist.
    const hash = window.location.hash ? decodeURIComponent(window.location.hash.slice(1)) : "";
    if (hash) {
      const target = document.getElementById(hash);
      if (target) target.scrollIntoView({ block: "start" });
    }
  }, [html]);

  return (
    <div className="soail-page">
      <Helmet>
        <title>Fleet Routing Without Memory | Helia Jamshidi</title>
        <meta
          name="description"
          content="The state of AI and machine learning for vehicle routing, fleet management and road logistics, to September 2026: learned solvers, routing from a fleet's own history, modeling the real day, travel-time matrices, the data, and the market."
        />
        <meta
          name="keywords"
          content="vehicle routing, VRP, fleet routing, machine learning, HGS, PyVRP, travel time matrix, logistics AI"
        />
        <meta name="author" content="Helia Jamshidi" />
        <meta property="og:title" content="Fleet Routing Without Memory | Helia Jamshidi" />
        <meta
          property="og:description"
          content="Learned solvers, routing from a fleet's own history, modeling the real day, learned travel-time matrices, the data that exists, and the market that sells it."
        />
        <meta property="og:type" content="article" />
        <meta
          property="og:url"
          content="https://heliajamshidi.me/ai-reports/state-of-ai-in-logistics"
        />
        <link
          rel="canonical"
          href="https://heliajamshidi.me/ai-reports/state-of-ai-in-logistics"
        />
      </Helmet>

      <div className="ai-report-banner">
        <strong>AI-researched report</strong>
        <span>Worth sharing, with that caveat.</span>
      </div>
      <header className="soail-masthead">
        <p className="soail-eyebrow">Research note · September 2026</p>
        <h1>Fleet routing without memory</h1>
      </header>

      <p className="soail-companion">
        <strong>New to metaheuristics, supervised or reinforcement learning for routing?</strong> Start with the
        companion course, <Link to="/writing/technology/learning-to-route">Learning to Route</Link>: the concepts, with labs,
        and what the evidence below adds to each.
      </p>

      <main className="soail-doc" ref={docRef} dangerouslySetInnerHTML={{ __html: html }} />

      <p className="soail-foot">
        Sources are named inline. Every number carries the label of how well it is known, and
        where a number is not sourced, it is not here.
      </p>
    </div>
  );
}
